import type {
  BoardTile,
  FactionDefinition,
  FactionId,
  Grade,
  TechCardDefinition,
  TechCardId,
  UnitDefinition,
  UnitTypeId,
} from "../types";

export const BOARD_SIZE = 6;
export const TRI_ANGER_VERSION = "tri-anger-0.1.0";
export const CAPTURE_VICTORY_COUNT = 8;
export const TECH_COIN_COST = 8;
export const MULTI_BUILD_COST = 5;
export const MULTI_BUILD_REQUIRED_COINS = 3;
export const HOME_INCOME = 5;
export const MULTI_INCOME = 5;

export const FIELD_LIMIT_BY_GRADE: Readonly<Record<Grade, number>> = {
  basic: 8,
  mid: 4,
  adv: 2,
};

export const FACTIONS: Readonly<Record<FactionId, FactionDefinition>> = {
  ECSU: {
    id: "ECSU",
    name: "ECSU",
    color: "#2563eb",
    style: "build / long game",
    description: "Defensive builders with workers and structures.",
  },
  GO: {
    id: "GO",
    name: "GO",
    color: "#dc2626",
    style: "speed / disruption",
    description: "Fast raiders that pressure low-level targets.",
  },
  SB: {
    id: "SB",
    name: "SB",
    color: "#16a34a",
    style: "resources / firepower",
    description: "Resource-forward mechanized units with heavy bodies.",
  },
};

export const UNIT_DEFINITIONS: Readonly<Record<UnitTypeId, UnitDefinition>> = {
  TURTLE: {
    id: "TURTLE",
    faction: "ECSU",
    name: "터틀",
    grade: "basic",
    level: 3,
    hp: 3,
    atk: 1,
    cost: 5,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "Defender combat ATK +1.",
  },
  WORKER: {
    id: "WORKER",
    faction: "ECSU",
    name: "워커",
    grade: "basic",
    level: 1,
    hp: 2,
    atk: 1,
    cost: 5,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "Can place ECSU structures at its current position.",
  },
  BOMB: {
    id: "BOMB",
    faction: "ECSU",
    name: "폭약",
    grade: "basic",
    level: 0,
    hp: 1,
    atk: 5,
    cost: 3,
    mov: 0,
    structure: true,
    selectable: false,
    summary: "Structure. Attacks low-level units, then is removed.",
  },
  TALDI: {
    id: "TALDI",
    faction: "ECSU",
    name: "탈디",
    grade: "mid",
    level: 5,
    hp: 8,
    atk: 3,
    cost: 12,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "Reliable mid-grade body.",
  },
  INVOLVE: {
    id: "INVOLVE",
    faction: "ECSU",
    name: "인볼브",
    grade: "mid",
    level: 7,
    hp: 3,
    atk: 0,
    cost: 7,
    mov: 1,
    structure: false,
    selectable: true,
    summary: "Removes the highest-level enemy at level 3 or below.",
  },
  TOWER: {
    id: "TOWER",
    faction: "ECSU",
    name: "타워",
    grade: "mid",
    level: 4,
    hp: 5,
    atk: 2,
    cost: 8,
    mov: 0,
    structure: true,
    selectable: false,
    summary: "Structure. Fires when enemies are level 4 or below.",
  },
  BOMBARD: {
    id: "BOMBARD",
    faction: "ECSU",
    name: "봄바드",
    grade: "adv",
    level: 2,
    hp: 3,
    atk: 0,
    cost: 20,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "Advanced support artillery.",
  },
  SHELL: {
    id: "SHELL",
    faction: "SB",
    name: "쉘",
    grade: "basic",
    level: 2,
    hp: 3,
    atk: 1,
    cost: 7,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "Adds +1 income on controlled income regions.",
  },
  ANT: {
    id: "ANT",
    faction: "SB",
    name: "앤트",
    grade: "basic",
    level: 1,
    hp: 1,
    atk: 1,
    cost: 3,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "Three or more ants in one combat set their ATK to 0.",
  },
  DESTROYER: {
    id: "DESTROYER",
    faction: "SB",
    name: "디스트로이어",
    grade: "mid",
    level: 7,
    hp: 10,
    atk: 3,
    cost: 17,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "Attacks twice.",
  },
  BISHOP: {
    id: "BISHOP",
    faction: "SB",
    name: "비숍",
    grade: "mid",
    level: 5,
    hp: 1,
    atk: 0,
    cost: 10,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "Supports Destroyer ATK in the same combat.",
  },
  SNEAKY: {
    id: "SNEAKY",
    faction: "SB",
    name: "스니키",
    grade: "mid",
    level: 4,
    hp: 2,
    atk: 2,
    cost: 10,
    mov: 3,
    structure: false,
    selectable: true,
    summary: "Can pass through enemy units.",
  },
  TITAN: {
    id: "TITAN",
    faction: "SB",
    name: "타이탄",
    grade: "adv",
    level: 8,
    hp: 15,
    atk: 3,
    cost: 25,
    mov: 1,
    structure: false,
    selectable: true,
    summary: "Large advanced threat.",
  },
  DEFY: {
    id: "DEFY",
    faction: "GO",
    name: "디파이",
    grade: "basic",
    level: 1,
    hp: 3,
    atk: 1,
    cost: 6,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "ATK +1 when all enemies are level 3 or below.",
  },
  SONIC: {
    id: "SONIC",
    faction: "GO",
    name: "소닉",
    grade: "basic",
    level: 2,
    hp: 2,
    atk: 1,
    cost: 4,
    mov: 3,
    structure: false,
    selectable: true,
    summary: "Fast basic unit with swarm HP tension.",
  },
  LATENCY: {
    id: "LATENCY",
    faction: "GO",
    name: "레이턴시",
    grade: "mid",
    level: 3,
    hp: 2,
    atk: 5,
    cost: 12,
    mov: 4,
    structure: false,
    selectable: true,
    summary: "SP attacks the lowest-level enemy once.",
  },
  BANDIT: {
    id: "BANDIT",
    faction: "GO",
    name: "밴딧",
    grade: "mid",
    level: 4,
    hp: 3,
    atk: 2,
    cost: 12,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "Steals gold equal to enemy highest level after combat.",
  },
  JOKER: {
    id: "JOKER",
    faction: "GO",
    name: "조커",
    grade: "mid",
    level: 5,
    hp: 3,
    atk: 10,
    cost: 12,
    mov: 2,
    structure: false,
    selectable: true,
    summary: "SP attacks level 4+ enemies once.",
  },
  PRIVE: {
    id: "PRIVE",
    faction: "GO",
    name: "프라이비",
    grade: "adv",
    level: 6,
    hp: 3,
    atk: 0,
    cost: 15,
    mov: 3,
    structure: false,
    selectable: true,
    summary: "Reveals opponent tech in the full rules.",
  },
};

export const TECH_CARD_DEFINITIONS: Readonly<
  Record<TechCardId, TechCardDefinition>
> = {
  ECSU_GRASSROOTS: {
    id: "ECSU_GRASSROOTS",
    faction: "ECSU",
    name: "풀뿌리 민주주의",
    grade: "mid",
    summary: "Worker ATK +1.",
  },
  ECSU_BLOOD_CURTAIN: {
    id: "ECSU_BLOOD_CURTAIN",
    faction: "ECSU",
    name: "피의 장막",
    grade: "mid",
    summary: "Turtle level +3.",
  },
  ECSU_THORNY_ROAD: {
    id: "ECSU_THORNY_ROAD",
    faction: "ECSU",
    name: "가시밭길",
    grade: "mid",
    summary: "Bomb can hit up to three level 2 or lower enemies.",
  },
  ECSU_TURTLE_SHIP: {
    id: "ECSU_TURTLE_SHIP",
    faction: "ECSU",
    name: "거북선",
    grade: "mid",
    summary: "Turtle HP +2.",
  },
  ECSU_ANTI_TANK_BOMB: {
    id: "ECSU_ANTI_TANK_BOMB",
    faction: "ECSU",
    name: "대전차용 폭약",
    grade: "mid",
    summary: "Bomb can target level 4 or lower enemies.",
  },
  ECSU_FOR_FEDERATION: {
    id: "ECSU_FOR_FEDERATION",
    faction: "ECSU",
    name: "칸연방을 위하여",
    grade: "adv",
    summary: "Taldi attacks twice.",
  },
  ECSU_MONGMONG: {
    id: "ECSU_MONGMONG",
    faction: "ECSU",
    name: "몽몽",
    grade: "adv",
    summary: "Involve HP +5.",
  },
  ECSU_DARK_TOWER: {
    id: "ECSU_DARK_TOWER",
    faction: "ECSU",
    name: "암흑기의 거탑",
    grade: "adv",
    summary: "Tower ATK +2.",
  },
  SB_SPICY_PEPPER: {
    id: "SB_SPICY_PEPPER",
    faction: "SB",
    name: "작은 고추가 맵나?",
    grade: "mid",
    summary: "Ant ATK +1.",
  },
  SB_SCARY_SHELL: {
    id: "SB_SCARY_SHELL",
    faction: "SB",
    name: "껍데기는 두렵다",
    grade: "mid",
    summary: "Shell HP +2.",
  },
  SB_MONEY_RAIN: {
    id: "SB_MONEY_RAIN",
    faction: "SB",
    name: "하늘에서 돈이 내려와",
    grade: "mid",
    summary: "Reveal to gain 10 gold.",
  },
  SB_CLIMBING_DENIED: {
    id: "SB_CLIMBING_DENIED",
    faction: "SB",
    name: "어딜 기어 올라?",
    grade: "mid",
    summary: "Ant support effect for Destroyer.",
  },
  SB_GOLDEN_TEMPLE: {
    id: "SB_GOLDEN_TEMPLE",
    faction: "SB",
    name: "황금사원",
    grade: "mid",
    summary: "Reveal to complete construction on an occupied M.",
  },
  SB_DESTROY_ALL: {
    id: "SB_DESTROY_ALL",
    faction: "SB",
    name: "파괴한다. 모든 것",
    grade: "adv",
    summary: "Destroyer HP +3.",
  },
  SB_BLESSING: {
    id: "SB_BLESSING",
    faction: "SB",
    name: "축복합니다",
    grade: "adv",
    summary: "Bishop movement support.",
  },
  SB_AMBUSH: {
    id: "SB_AMBUSH",
    faction: "SB",
    name: "기습",
    grade: "adv",
    summary: "Sneaky ATK +2.",
  },
  GO_MAINTENANCE: {
    id: "GO_MAINTENANCE",
    faction: "GO",
    name: "정비가 필요합니다",
    grade: "mid",
    summary: "Defy ATK +1.",
  },
  GO_REVOLUTION_FROM_BELOW: {
    id: "GO_REVOLUTION_FROM_BELOW",
    faction: "GO",
    name: "아래로부터의 혁명",
    grade: "mid",
    summary: "Defy attacks three times when enemies are level 3 or lower.",
  },
  GO_FORCE_EQUALS_MA: {
    id: "GO_FORCE_EQUALS_MA",
    faction: "GO",
    name: "F=ma",
    grade: "mid",
    summary: "Sonic ATK +1.",
  },
  GO_FLY_UP: {
    id: "GO_FLY_UP",
    faction: "GO",
    name: "날아올라~",
    grade: "mid",
    summary: "Defy level support with Latency.",
  },
  GO_ASSASSINATE: {
    id: "GO_ASSASSINATE",
    faction: "GO",
    name: "암살하라",
    grade: "mid",
    summary: "Sonic can attack the lowest-level enemy once.",
  },
  GO_UNFINISHED_REVOLUTION: {
    id: "GO_UNFINISHED_REVOLUTION",
    faction: "GO",
    name: "끝이나지 않은 혁명",
    grade: "adv",
    summary: "Defy ATK +1.",
  },
  GO_DARK_PATH: {
    id: "GO_DARK_PATH",
    faction: "GO",
    name: "암흑의 길을 걷는다",
    grade: "adv",
    summary: "Low-level enemies cannot attack Latency.",
  },
  GO_HEAVY_ARMOR: {
    id: "GO_HEAVY_ARMOR",
    faction: "GO",
    name: "중무장",
    grade: "adv",
    summary: "Bandit HP +3.",
  },
};

const GRID: readonly (readonly string[])[] = [
  ["M", "H", "E", "1", "E", "2"],
  ["H", "H", "E", "E", "0", "E"],
  ["E", "E", "3", "X", "E", "1"],
  ["1", "E", "X", "3", "E", "E"],
  ["E", "0", "E", "E", "H", "H"],
  ["2", "E", "1", "E", "H", "M"],
];

export function createDefaultBoard(
  playerOneId: string,
  playerTwoId: string,
): readonly BoardTile[] {
  return GRID.flatMap((row, y) =>
    row.map((raw, x): BoardTile => {
      const position = { x, y };
      if (raw === "X") {
        return { position, kind: "blocked", income: 0 };
      }
      if (raw === "M") {
        return { position, kind: "multi", income: MULTI_INCOME };
      }
      if (raw === "H") {
        const homeOwnerId = x <= 1 && y <= 1 ? playerOneId : playerTwoId;
        return { position, kind: "home", income: HOME_INCOME, homeOwnerId };
      }
      if (raw === "E") {
        return { position, kind: "empty", income: 0 };
      }
      return { position, kind: "capture", income: Number(raw) };
    }),
  );
}

export function defaultUnitDeckForFaction(
  faction: FactionId,
): readonly UnitTypeId[] {
  const units = Object.values(UNIT_DEFINITIONS).filter(
    (unit) => unit.faction === faction && unit.selectable,
  );
  const byGrade = (grade: Grade, limit: number) =>
    units
      .filter((unit) => unit.grade === grade)
      .slice(0, limit)
      .map((unit) => unit.id);
  return [...byGrade("basic", 2), ...byGrade("mid", 2), ...byGrade("adv", 1)];
}

export function defaultTechDeckForFaction(
  faction: FactionId,
): readonly TechCardId[] {
  const techCards = Object.values(TECH_CARD_DEFINITIONS).filter(
    (card) => card.faction === faction,
  );
  return [
    ...techCards
      .filter((card) => card.grade === "mid")
      .slice(0, 3)
      .map((card) => card.id),
    ...techCards
      .filter((card) => card.grade === "adv")
      .slice(0, 3)
      .map((card) => card.id),
  ];
}
